<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import ExcelGrid from '@/components/base/grid/ExcelGrid.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import ModalDeBusca from '@/components/base/modal/ModalDeBusca.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import SelecaoProdutoEstoqueModal from './SelecaoProdutoEstoqueModal.vue'
import api from '@/services/api'
import { criarConfigBuscaUnidadeMedidaProduto } from '@/search-definitions/produtoUnidadeMedidaSearch'
import { criarConfigBuscaUnidadeMedidaCliente } from '@/search-definitions/unidadeMedidaClienteSearch'
import { PED_VEN_ITEM_ST } from '@/constants/PED_VEN_ITEM_ST'
import { PED_VEN_WORKFLOW_ST } from '@/constants/PED_VEN_WORKFLOW_ST'
import { PED_VEN_MODO_CUSTEIO } from '@/constants/PED_VEN_MODO_CUSTEIO'
import { ESTQ_TIPO_LOCAL_ESTOCAGEM_CUSTOM } from '@/constants/ESTQ_TIPO_LOCAL_ESTOCAGEM.Custom'
import { PED_VEN_MODO_PRECIFIC } from '@/constants/PED_VEN_MODO_PRECIFIC'
import { useMetadataListasStore } from '@/stores/metadataListas'

const props = defineProps({
  itens: { type: Array, default: () => [] },
  listas: { type: Object, default: () => ({}) },
  podeEditar: { type: Boolean, default: false },
  isPosAprovacao: { type: Boolean, default: false },
  // Bloqueia a edição das linhas enquanto o pedido está em aprovação comercial.
  bloqueadoAprovacao: { type: Boolean, default: false },
  // Pedido read-only por WORKFLOW (status diferente de Em Digitação). Bloqueia
  // as colunas de negócio individualmente, mantendo as exceções editáveis
  // (status e observação do item).
  readOnlyWorkflow: { type: Boolean, default: false },
  lstMoeda: { type: String, default: '' },
  lstMoedaPadrao: { type: String, default: '' },
  lstMoedaGerencial: { type: String, default: '' },
  idPedidoVenda: { type: Number, required: true },
  idFilial: { type: Number, default: null },
  descricaoFilial: { type: String, default: '' },
  idOrganizacao: { type: Number, default: null },
  idLocalEstoquePadrao: { type: Number, default: null },
  descricaoLocalEstoquePadrao: { type: String, default: '' },
  dataPrevisaoEntregaPadrao: { type: String, default: null },
  taxaCambioPadrao: { type: Number, default: null },
  taxaCambioGerencial: { type: Number, default: null },
  lstModoPrecificacao: { type: String, default: null },
  idPrecoComercial: { type: Number, default: null },
  lstStatusWorkflowPedido: { type: String, default: '' },
  // Status da reserva de estoque do documento (GET /api/ReservasEstoque/por-documento).
  // O PV não orquestra reserva — apenas reflete o estado para exibição no modal do item.
  statusReservaDocumento: { type: Object, default: null }
})

const emit = defineEmits(['save-item', 'delete-item', 'cancel-item', 'show-notification', 'save-all-items'])

// Deriva o modo de custeio a partir do tipo do local de estoque (classe Custom).
//   • Trânsito → CustoSimulado (custo digitado pelo usuário).
//   • Final    → CustoDaControladoria (custo vigente resolvido no backend).
const derivarModoCusteio = (tipoLocal) => {
  if (ESTQ_TIPO_LOCAL_ESTOCAGEM_CUSTOM.ehEstoqueTransito(tipoLocal)) return PED_VEN_MODO_CUSTEIO.CustoSimulado
  if (ESTQ_TIPO_LOCAL_ESTOCAGEM_CUSTOM.ehEstoqueFinal(tipoLocal)) return PED_VEN_MODO_CUSTEIO.CustoDaControladoria
  return null
}

// Indica se a linha está no modo de custeio simulado (custo digitável).
// Serviços (isServico) também entram como CustoSimulado — independentemente do
// LstModoCusteio retornado pelo backend (dados legados/placeholders retornam null).
const ehCusteioSimulado = (row) =>
  row?.lstModoCusteio === PED_VEN_MODO_CUSTEIO.CustoSimulado || row?.isServico === true

// A coluna CUST SIMUL só é exibida quando há ao menos um item em modo simulado
// ou quando há serviços (que também usam custo simulado).
// (Em tabela não é possível ocultar a coluna por linha; no modo Final ela fica oculta.)
const temItemSimulado = computed(() => itensLocal.value.some(r => ehCusteioSimulado(r) || r?.isServico === true))

// ============================================================
// Modo de Precificação — comportamento dinâmico das colunas
// ============================================================
const isReadOnly = computed(() =>
  !props.podeEditar || props.isPosAprovacao || props.bloqueadoAprovacao || props.readOnlyWorkflow
)

// Campos do item que permanecem editáveis mesmo quando o pedido está read-only.
const CAMPOS_SEMPRE_EDITAVEIS_ITEM = ['lstStatusPedidoItem', 'observacao']

/// Helper global de bloqueio por coluna do item.
/// - Sem permissão (RBAC) ou read-only (workflow/aprovação) → bloqueado.
/// - Exceções (status, observação, tolerância) permanecem editáveis.
const campoEditavelItem = (campo) => {
  if (CAMPOS_SEMPRE_EDITAVEIS_ITEM.includes(campo)) return true
  return !isReadOnly.value
}

/// Indica se a linha do item está CANCELADA (status terminal do item).
/// Linhas canceladas são exibidas em modo somente-leitura e ficam isentas da
/// validação de obrigatoriedade: quantidade/preço zerados são estados legítimos
/// (o backend move a quantidade para `QuantidadeCancelada` ao cancelar).
const isLinhaCancelada = (row) =>
  row?.lstStatusPedidoItem === PED_VEN_ITEM_ST.Cancelado

/// Helper de bloqueio por coluna do item considerando o status da LINHA:
/// uma linha CANCELADA nunca é editável nas colunas de negócio do grid
/// (as exceções status/observação permanecem acessíveis via `campoEditavelItem`).
const campoEditavelItemLinha = (row, campo) => {
  if (isLinhaCancelada(row)) return false
  return campoEditavelItem(campo)
}

const isModoPorTabela = computed(() =>
  props.lstModoPrecificacao === PED_VEN_MODO_PRECIFIC.PrecificacaoPorTabela
)

const isModoProjeto = computed(() =>
  props.lstModoPrecificacao === PED_VEN_MODO_PRECIFIC.PrecificacaoDeProjeto
)

// PREÇO UNIT editável no modo Projeto (ou em pedidos legados sem modo definido).
const precoUnitEditavel = computed(() =>
  (isModoProjeto.value || !props.lstModoPrecificacao) && !isReadOnly.value
)

// Desconto editável no modo Por Tabela (ou em pedidos legados sem modo definido).
const descontoEditavel = computed(() =>
  (isModoPorTabela.value || !props.lstModoPrecificacao) && !isReadOnly.value
)

const itensLocal = ref([])

// Mapa de âncoras da reserva por IdDocumentoOrigemItem (= idPedidoVendaProduto).
// Fonte: props.statusReservaDocumento (GET /api/ReservasEstoque/por-documento).
// As âncoras NÃO vivem em PedidoVendaProduto — são derivadas da ReservaEstoqueItem.
// Ao carregar os itens, enriquecemos cada linha com as âncoras da reserva para que
// o grid exiba e preserve a origem (inclusive itens de pronta entrega, onde Filial +
// Local Estoque são âncoras suficientes).
const ancorasReservaPorItem = computed(() => {
  const mapa = {}
  const itensReserva = props.statusReservaDocumento?.itens || []
  for (const ri of itensReserva) {
    if (ri.idDocumentoOrigemItem != null) {
      mapa[ri.idDocumentoOrigemItem] = ri
    }
  }
  return mapa
})

// Aplica as âncoras da reserva a uma linha do grid (se ainda não tiver sido
// preenchida pelo modal). Preserva o que o usuário já escolheu no modal.
const aplicarAncorasReservaNaLinha = (item) => {
  const res = ancorasReservaPorItem.value[item.idPedidoVendaProduto]
  if (!res) return item
  const enriquecido = { ...item }
  if (enriquecido.idEmbarque == null) enriquecido.idEmbarque = res.idEmbarque ?? null
  if (enriquecido.idEmbarqueContainer == null) enriquecido.idEmbarqueContainer = res.idEmbarqueContainer ?? null
  if (enriquecido.idEmbarquePackingListProduto == null) enriquecido.idEmbarquePackingListProduto = res.idEmbarquePackingListProduto ?? null
  if (enriquecido.idLoteFabricacao == null) enriquecido.idLoteFabricacao = res.idLoteFabricacao ?? null
  if (enriquecido.idDocumentoEntrada == null) enriquecido.idDocumentoEntrada = res.idDocumentoEntrada ?? null
  if (enriquecido.idDocumentoEntradaItem == null) enriquecido.idDocumentoEntradaItem = res.idDocumentoEntradaItem ?? null
  return enriquecido
}

watch(() => props.itens, (novos) => {
  const novosArr = novos || []
  // Preserva o estado local das linhas ainda não persistidas (pendentes ou sem id),
  // evitando que o recarregamento do pai reverta alterações locais (ex: modo de custeio
  // de uma linha em Trânsito cujo custo obrigatório ainda não foi preenchido).
  const pendentesPorId = new Map(
    itensLocal.value
      .filter(r => r.idPedidoVendaProduto && r._pendingSave)
      .map(r => [r.idPedidoVendaProduto, r])
  )
  const semId = itensLocal.value.filter(r => !r.idPedidoVendaProduto)

  const persistidas = novosArr.map(item => {
    const local = pendentesPorId.get(item.idPedidoVendaProduto)
    // Preserva o estado local, mas adota o xmin do servidor — o backend já
    // incrementou o token de concorrência no último salvamento (evita 409).
    const base = local ? { ...local, xmin: item.xmin } : { ...item, _pendingSave: false }
    // Enriquece com as âncoras da reserva (fonte da verdade da origem).
    return aplicarAncorasReservaNaLinha(base)
  })

  itensLocal.value = [...persistidas, ...semId]
}, { deep: true, immediate: true })

// Quando o status da reserva chega (carregamento assíncrono) ou muda, reaplica as
// âncoras nas linhas já carregadas que ainda não foram preenchidas pelo modal.
watch(() => props.statusReservaDocumento, () => {
  itensLocal.value = itensLocal.value.map(item => aplicarAncorasReservaNaLinha(item))
}, { deep: true })

const locaisPorProduto = ref({})
const unidadesPorProduto = ref({})
const isLoadingAcao = ref(false)
const excelGridRef = ref(null)

// Mantém a coluna CUST SIMUL visível enquanto houver ao menos uma linha em
// modo simulado (Trânsito). Controlado programaticamente para garantir a
// reatividade independentemente do gerenciador de layout.
watch(temItemSimulado, (visivel) => {
  nextTick(() => {
    excelGridRef.value?.setColumnVisible('custoUnitarioSnapshot', visivel)
  })
}, { immediate: true })

// Devolve o foco à célula lookup que abriu o modal, para que a navegação de
// teclado do grid continue exatamente a partir daquela célula.
const focarCelulaLookup = (rowIndex, field) => {
  if (rowIndex == null || rowIndex < 0) return
  nextTick(() => {
    excelGridRef.value?.focusCellByField(rowIndex, field)
  })
}

const showBuscaUnidadeModal = ref(false)
const buscaUnidadeRowIndex = ref(null)
const configBuscaUnidade = ref(null)

const showItemModal = ref(false)
const itemModalData = ref(null)
const itemModalRowIndex = ref(null)

// Snapshot dos campos EDITÁVEIS do modal de detalhes, capturado ao abrir.
// Comparado com o estado atual para decidir se há alterações não salvas —
// evita o ConfirmationModal indevido ao fechar sem editar nada (o
// ModalFormularioV2 só pergunta com confirmar-ao-sair quando temAlteracoes=true).
const itemModalSnapshot = ref(null)

// true quando o usuário alterou algum campo editável do modal de detalhes
// (Tolerância % / Observação) em relação ao estado original da linha.
const itemModalTemAlteracoes = computed(() => {
  const dados = itemModalData.value
  const snap = itemModalSnapshot.value
  if (!dados || !snap) return false
  const normalizarTolerancia = (v) => {
    if (v === '' || v === null || v === undefined || Number.isNaN(Number(v))) return null
    return Number(v)
  }
  return normalizarTolerancia(dados.percentualTolerancia) !== normalizarTolerancia(snap.percentualTolerancia) ||
    ((dados.observacao ?? '') !== (snap.observacao ?? ''))
})

// Dados derivados para o modal de detalhes (read-only): local selecionado e seu
// saldo disponível, obtidos do mapa `locaisPorProduto` (já carregado por
// `carregarDadosProduto`). Exibidos apenas como informação — sem novo endpoint.
const localSelecionadoModal = computed(() => {
  const row = itemModalData.value
  if (!row?.idProduto || !row?.idLocalEstoque) return null
  return (locaisPorProduto.value[row.idProduto] || [])
    .find(l => l.idLocalEstoque === row.idLocalEstoque) || null
})
const saldoLocalSelecionado = computed(() => localSelecionadoModal.value?.quantidadeDisponivel ?? null)
// O item possui PRÉ-CONFIGURAÇÃO DE RESERVA quando tem âncoras de origem (embarque /
// PLP / contêiner / lote) escolhidas no modal de seleção de produto/estoque.
//   • Com pré-configuração → exibe a seção "Pré-configuração da reserva".
//   • Sem pré-configuração → exibe a seção "Saldo".
// (Na arquitetura-alvo, a reserva efetiva é uma ReservaEstoque exibida neste mesmo
//  bloco com badge de status — ver arquitetura_reserva_estoque.md.)
const itemReservado = computed(() => {
  const d = itemModalData.value
  if (!d) return false
  return d.idEmbarque != null || d.idEmbarquePackingListProduto != null ||
    d.idEmbarqueContainer != null || d.idLoteFabricacao != null
})

// Título do card de reserva conforme o estado do item (mesma ramificação do template).
const tituloCardReserva = computed(() => {
  if (reservaDoItem.value) return 'Reserva de Estoque'
  if (itemReservado.value) return 'Pré-configuração da Reserva'
  return 'Local e Saldo'
})

// Linha da reserva de estoque correspondente ao item do modal (por IdDocumentoOrigemItem).
// Fonte: `props.statusReservaDocumento` (ReservaDocumentoStatusDto), carregado pelo pai via
// GET /api/ReservasEstoque/por-documento. Exibe o status efetivo da reserva (PreConfigurada,
// Ativa, Consumida, Cancelada) — o PV apenas reflete o estado, não orquestra a reserva.
const reservaDoItem = computed(() => {
  const d = itemModalData.value
  if (!d?.idPedidoVendaProduto || !props.statusReservaDocumento?.itens) return null
  return props.statusReservaDocumento.itens
    .find(i => i.idDocumentoOrigemItem === d.idPedidoVendaProduto) || null
})

// Rótulo da quantidade reservada exibindo a unidade de medida BASE — a reserva é
// controlada em unidade base (quantidadeReservadaUnBasica), conforme a arquitetura
// da reserva de estoque. O texto da unidade é resolvido pelo Dicionário de Dados
// (ReservaEstoqueItem.LstUnidadeMedidaBasica → UN_MED). Fallback para o código
// quando a lista não resolve (resolverTextoLista já devolve o código).
const labelQtdReservadaReserva = computed(() => {
  const unidade = reservaDoItem.value?.lstUnidadeMedidaBasica
  if (!unidade) return 'Qtd Reservada'
  const texto = resolverTextoLista(listasReserva.value.LstUnidadeMedidaBasica, unidade)
  return `Qtd Reservada (${texto})`
})

// Listas resolvidas pelo Dicionário de Metadados (regra do dicionário de dados),
// sem chamar o endpoint de lookup diretamente:
//   • PedidoVenda.LstMoeda*             → nomes das moedas na tabela de valores.
//   • PedidoVendaProduto.LstModoCusteio → rótulo do modo de custeio no modal.
//   • LocalEstoque.LstTipoLocal         → rótulo do tipo do local no modal.
const metadataStore = useMetadataListasStore()
const listasMoeda = ref({ LstMoeda: [], LstMoedaPadrao: [], LstMoedaGerencial: [] })
const listasItem = ref({ LstModoCusteio: [], LstTipoLocal: [], LstStatusPedidoItem: [] })
const listasReserva = ref({ LstStatusItemReserva: [], LstStatusReserva: [], LstUnidadeMedidaBasica: [] })

onMounted(async () => {
  const result = await metadataStore.carregarListasParaTabelas([
    'PedidoVenda',
    'PedidoVendaProduto',
    'LocalEstoque',
    'ReservaEstoqueItem'
  ])
  listasMoeda.value = {
    LstMoeda: result.LstMoeda || [],
    LstMoedaPadrao: result.LstMoedaPadrao || [],
    LstMoedaGerencial: result.LstMoedaGerencial || []
  }
  listasItem.value = {
    LstModoCusteio: result.LstModoCusteio || [],
    LstTipoLocal: result.LstTipoLocal || [],
    LstStatusPedidoItem: result.LstStatusPedidoItem || []
  }
  listasReserva.value = {
    LstStatusItemReserva: result.LstStatusItemReserva || [],
    LstStatusReserva: result.LstStatusReserva || [],
    // ReservaEstoqueItem.LstUnidadeMedidaBasica → UN_MED (Dicionário de Dados).
    LstUnidadeMedidaBasica: result.LstUnidadeMedidaBasica || []
  }
})

const deleteTargetRow = ref(null)

// Modal de seleção de produto/estoque (acionado pela lupinha do SKU)
const showModalSelecaoEstoque = ref(false)
const selecaoEstoqueRowIndex = ref(null)
const selecaoEstoqueRow = computed(() => {
  const idx = selecaoEstoqueRowIndex.value
  return idx != null && idx >= 0 ? itensLocal.value[idx] : null
})

// Tipo do local de estoque da linha (para o modal abrir no modo correto).
const selecaoEstoqueRowTipoLocal = computed(() => {
  const row = selecaoEstoqueRow.value
  if (!row) return null
  const local = (locaisPorProduto.value[row.idProduto] || [])
    .find(l => l.idLocalEstoque === row.idLocalEstoque)
  return local?.lstTipoLocal ?? null
})

// Busca de produtos para o autocomplete do SKU.
// A lista suspensa NÃO define o local de estoque: quando a linha já tem local,
// ele é usado como FILTRO para exibir o saldo disponível e bloquear a seleção
// de produtos sem saldo. Sem local, não há consistência de estoque.
const buscarProdutosAutocomplete = async (termo, ctx) => {
  const idLocalEstoque = ctx?.row?.idLocalEstoque || null
  const response = await api.get('/Produtos/autocomplete-busca', {
    params: {
      busca: termo,
      pageSize: 15,
      idFilial: props.idFilial || null,
      idLocalEstoque
    },
    _suppressLoading: true
  })
  const itens = response.data || []
  return itens.map(p => {
    const item = { ...p }
    // Só exibe o saldo quando há local selecionado (senão não há consistência).
    if (p.saldoDisponivelNoLocal != null) {
      item.saldoTexto = `Saldo: ${Number(p.saldoDisponivelNoLocal).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`
    }
    return item
  })
}

const colunas = computed(() => [
  {
    field: 'sku',
    label: 'SKU',
    type: 'autocomplete',
    lookupConfig: { descField: 'sku', idField: 'idProduto' },
    autocompleteConfig: {
      minChars: 2,
      debounceMs: 350,
      maxResults: 15,
      displayField: 'sku',
      idField: 'idProduto',
      secondaryFields: ['nome', 'saldoTexto'],
      requireSelection: true,
      mensagemInvalido: 'O SKU "{valor}" é inválido e foi removido.',
      searchFn: buscarProdutosAutocomplete,
      // Bloqueia a seleção quando o produto não tem saldo no local já selecionado.
      disabledItem: (item) => item.saldoDisponivelNoLocal != null && item.saldoDisponivelNoLocal <= 0
    },
    width: '15%',
    // Linhas canceladas são terminais: SKU não é obrigatório nem editável
    // (quantidade/preço zerados são estados legítimos após o cancelamento).
    required: (row) => !isLinhaCancelada(row),
    // Editável quando o grid não está bloqueado (campoEditavelItemLinha cobre
    // read-only do workflow/aprovação/RBAC e linha cancelada). O autocomplete
    // funciona para todos os tipos de estoque — inclusive trânsito, onde o
    // usuário pode digitar o SKU diretamente ou usar a lupa (modal) para
    // selecionar produto com âncoras de reserva.
    editable: (row) => campoEditavelItemLinha(row, 'sku')
  },
  {
    field: 'descricaoProduto',
    label: 'Descrição',
    type: 'text',
    // Editável apenas para serviços (descrição livre do serviço) e em linhas não canceladas.
    editable: (row) => !isLinhaCancelada(row) && row?.isServico === true && campoEditavelItem('descricaoProduto'),
    width: '25%'
  },
  {
    field: 'idLocalEstoque',
    label: 'Estoque',
    type: 'lookup',
    lookupConfig: { descField: 'descricaoLocal', idField: 'idLocalEstoque' },
    width: '14%',
    // Serviços NÃO exigem e BLOQUEIAM a escolha de estoque (sem lupa/modal).
    // Linhas canceladas também ficam bloqueadas e isentas da obrigatoriedade.
    editable: (row) => !isLinhaCancelada(row) && row?.isServico !== true && campoEditavelItem('idLocalEstoque'),
    required: (row) => !isLinhaCancelada(row) && row?.isServico !== true
  },
  {
    field: 'lstUnidadeMedidaVenda',
    label: 'Un. Med.',
    type: 'lookup',
    lookupConfig: { descField: 'descricaoUnidade', idField: 'lstUnidadeMedidaVenda' },
    width: '6%',
    editable: (row) => campoEditavelItemLinha(row, 'lstUnidadeMedidaVenda')
  },
  {
    field: 'quantidade',
    label: 'Qtd',
    type: 'number',
    width: '5%',
    // Quantidade NÃO é obrigatória em linhas canceladas (o backend zera a
    // quantidade ao cancelar — o valor original migra para QuantidadeCancelada).
    required: (row) => !isLinhaCancelada(row),
    decimals: 4,
    editable: (row) => campoEditavelItemLinha(row, 'quantidade')
  },
  {
    field: 'custoUnitarioSnapshot',
    label: 'CUST UNIT SIMUL',
    type: 'number',
    width: '8%',
    decimals: 4,
    decimalsMinimo: 4,
    // Visibilidade em DUAS fontes COMPLEMENTARES (não redundantes):
    //   1. `visible` (declarativo/determinístico) — oculta a coluna quando não há
    //      itens simulados, independentemente do layout persistido.
    //   2. `watch(temItemSimulado)` + `setColumnVisible` (imperativo) — força a
    //      exibição quando surge um item simulado, sobrepondo um layout que a ocultou.
    // Editável e obrigatório apenas no modo CustoSimulado (estoque em trânsito).
    visible: temItemSimulado.value,
    editable: (row) => !isLinhaCancelada(row) && ehCusteioSimulado(row) && campoEditavelItem('custoUnitarioSnapshot'),
    required: (row) => !isLinhaCancelada(row) && ehCusteioSimulado(row)
  },
  {
    field: 'valorUnitarioOriginal',
    label: 'PREÇO UNIT',
    type: 'number',
    width: '7%',
    // PREÇO UNIT não é obrigatório em linhas canceladas (estado terminal).
    required: (row) => !isLinhaCancelada(row),
    decimals: 4,
    decimalsMinimo: 4,
    // Editável no modo Precificação de Projeto OU para serviços (preço digitado —
    // serviços não têm entrada na Tabela de Preços). O cálculo do valor é idêntico
    // ao do produto (recalcularLinha). Linhas canceladas nunca são editáveis.
    editable: (row) => !isLinhaCancelada(row) && (row?.isServico === true || precoUnitEditavel.value) && campoEditavelItem('valorUnitarioOriginal')
  },
  {
    field: 'valorTotalDesconto',
    label: 'Desconto',
    type: 'number',
    width: '7%',
    decimals: 4,
    decimalsMinimo: 4,
    // Editável apenas no modo Precificação por Tabela e em linhas não canceladas.
    editable: (row) => !isLinhaCancelada(row) && descontoEditavel.value && campoEditavelItem('valorTotalDesconto')
  },
  {
    field: 'valorTotalNegociado',
    label: 'Total',
    type: 'number',
    width: '8%',
    editable: false,
    decimals: 4,
    decimalsMinimo: 4
  },
  {
    field: 'dataPrevisaoEntrega',
    label: 'Prev. Entrega',
    type: 'date',
    width: '8%',
    editable: (row) => campoEditavelItemLinha(row, 'dataPrevisaoEntrega')
  },
  {
    field: 'lstStatusPedidoItem',
    label: 'Status',
    type: 'badge',
    badgeTextResolver: (row) => getStatusItemLabel(row?.lstStatusPedidoItem),
    badgeClassResolver: (row) => badgeStatusItem(row?.lstStatusPedidoItem),
    width: '15%'
  }
])

// Calcula a quantidade pendente do item: Quantidade - QuantidadeFaturada - QuantidadeCancelada.
// Segue a regra do brainstorm_cancelamento_item.md (Quantidade imutável pós-confirmação).
const calcularQtdPendente = (row) => {
  if (!row) return null
  return Math.max(0, (Number(row.quantidade) || 0) - (Number(row.quantidadeFaturada) || 0) - (Number(row.quantidadeCancelada) || 0))
}

const formatarMoeda = (valor) => {
  if (valor == null) return '-'
  // Campos de valores em moeda: no mínimo 4 casas decimais.
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4
  }).format(Number(valor) || 0)
}

// Formatação de quantidades/saldos (mínimo 4 casas, como o grid).
const formatarQtd = (valor) => {
  if (valor == null) return '-'
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4
  }).format(Number(valor) || 0)
}

const exibirId = (valor) => (valor != null && valor !== '' ? String(valor) : '-')

// Rótulo do status do item — resolvido pelo Dicionário de Metadados
// (PedidoVendaProduto.LstStatusPedidoItem → PED_VEN_ITEM_ST).
const getStatusItemLabel = (status) => resolverTextoLista(listasItem.value.LstStatusPedidoItem, status)

// Classe do badge conforme o texto do status (regras de cor por palavra-chave).
const badgeStatusItem = (status) => {
  const texto = String(getStatusItemLabel(status)).toUpperCase()
  if (texto.includes('ABERTO')) return 'badge-ghost-warning'
  if (texto.includes('CONFIRMADO')) return 'badge-ghost-info'
  if (texto.includes('FATURADO') || texto.includes('TOTAL')) return 'badge-ghost-success'
  if (texto.includes('CANCELADO')) return 'badge-ghost-danger'
  if (texto.includes('PARCIAL')) return 'badge-ghost-primary'
  return 'badge-ghost-secondary'
}

// Rótulo do status da reserva — Dicionário de Metadados (ReservaEstoqueItem → ESTQ_RESERVA_ST).
const getStatusReservaLabel = (status) => resolverTextoLista(listasReserva.value.LstStatusItemReserva, status)

// Classe do badge conforme o status da reserva (4 status atomizados).
const badgeStatusReserva = (status) => {
  const texto = String(getStatusReservaLabel(status)).toUpperCase()
  if (texto.includes('CONSUMID')) return 'badge-ghost-success'
  if (texto.includes('ATIVA')) return 'badge-ghost-info'
  if (texto.includes('CANCELAD')) return 'badge-ghost-danger'
  if (texto.includes('PRÉ-CONFIG') || texto.includes('PRECONFIG')) return 'badge-ghost-warning'
  return 'badge-ghost-secondary'
}

// Botões de ação para transição de status do item:
//   • "Cancelar Item" → disponível quando pedido está pós-aprovação comercial
//     (seq workflow >= AprovadoComercial) e item não está cancelado/faturado.
//     Antes da aprovação, o item deve ser excluído, não cancelado.
//   • "Encerrar Parcial" → disponível apenas quando status atual == FaturadoParcial
const podeCancelarItem = computed(() => {
  const row = itemModalData.value
  if (!row?.lstStatusPedidoItem) return false
  // Só permite cancelar se o pedido estiver pós-aprovação comercial
  const seqWorkflow = PED_VEN_WORKFLOW_ST.sequencia?.[props.lstStatusWorkflowPedido] || 0
  const seqAprovadoComercial = PED_VEN_WORKFLOW_ST.sequencia?.[PED_VEN_WORKFLOW_ST.AprovadoComercial] || 4
  if (seqWorkflow < seqAprovadoComercial) return false
  // Item já cancelado ou faturado não pode ser cancelado novamente
  if (row.lstStatusPedidoItem === PED_VEN_ITEM_ST.Cancelado) return false
  if (row.lstStatusPedidoItem === PED_VEN_ITEM_ST.FaturadoTotal ||
      row.lstStatusPedidoItem === PED_VEN_ITEM_ST.FaturadoParcial) return false
  return true
})

const podeEncerrarParcial = computed(() => {
  const row = itemModalData.value
  if (!row?.lstStatusPedidoItem) return false
  return row.lstStatusPedidoItem === PED_VEN_ITEM_ST.FaturadoParcial
})

const cancelarItemModalRef = ref(null)
const cancelarItemMessage = ref('')

const acaoCancelarItem = () => {
  if (!itemModalData.value?.idPedidoVendaProduto) return
  // Exibe modal de confirmação antes de chamar o backend
  cancelarItemMessage.value = 'Tem certeza que deseja cancelar este item? O cancelamento é irreversível.'
  if (cancelarItemModalRef.value) {
    cancelarItemModalRef.value.show()
  }
}

const onConfirmCancelarItem = async (confirmed) => {
  if (!confirmed) return
  const row = itemModalData.value
  if (!row?.idPedidoVendaProduto) return

  isLoadingAcao.value = true
  try {
    const response = await api.post(`/PedidoVendaProdutos/${row.idPedidoVendaProduto}/cancelar`)
    const data = response.data

    // Atualiza a linha local com os dados retornados
    if (data?.item) {
      Object.assign(row, data.item)
    }

    // Propaga o item atualizado para a LINHA REAL do grid (o `row` do modal é uma
    // cópia criada em abrirDetalhesItem — sem isso o status não muda na grade ao
    // fechar o modal). Mesmo padrão do onConfirmEncerrarParcial.
    const gridRow = itensLocal.value.find(r => r.idPedidoVendaProduto === row.idPedidoVendaProduto)
    if (gridRow) Object.assign(gridRow, data.item)

    // Notifica o usuário
    const msg = data?.message || 'Item cancelado com sucesso.'
    emit('show-notification', { message: msg, type: 'success' })

    // Se o status do pedido foi alterado, emite evento para o pai recarregar
    if (data?.statusPedidoAlterado) {
      emit('show-notification', {
        message: 'O pedido foi retornado para "Em Digitação" devido à margem insuficiente.',
        type: 'warning'
      })
    }

    // Notifica o pai para re-sincronizar itens, cabeçalho, status da reserva e
    // grid de aprovações (o cancelamento pode ter mudado totais/margem/workflow).
    emit('cancel-item', row.idPedidoVendaProduto)

    showItemModal.value = false
    itemModalData.value = null
    itemModalRowIndex.value = null
  } catch (error) {
    const errorData = error.response?.data
    let msg = 'Erro ao cancelar item.'
    if (typeof errorData === 'string') msg = errorData
    else if (errorData?.message) msg = errorData.message
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingAcao.value = false
  }
}

const encerrarItemModalRef = ref(null)
const encerrarItemMessage = ref('')

// "Encerrar Parcial" — item parcialmente faturado sem continuidade de consumo.
// Aciona o endpoint dedicado do backend (POST /PedidoVendaProdutos/{id}/encerrar),
// que libera todo o saldo Ativa da reserva via EncerrarItemReservaCommand
// (por IdDocumentoOrigemItem — o consumidor não calcula o saldo remanescente).
const acaoEncerrarParcial = () => {
  if (!itemModalData.value?.idPedidoVendaProduto) return
  encerrarItemMessage.value = 'Este item foi parcialmente faturado e o saldo restante não será faturado. O saldo da reserva de estoque será liberado (devolvido ao disponível) e o item passará para "Encerrado Parcial". Deseja continuar?'
  if (encerrarItemModalRef.value) {
    encerrarItemModalRef.value.show()
  }
}

const onConfirmEncerrarParcial = async (confirmed) => {
  if (!confirmed) return
  const row = itemModalData.value
  if (!row?.idPedidoVendaProduto) return
  isLoadingAcao.value = true
  try {
    // O endpoint retorna o DTO do item atualizado (regra de reatividade Vue 3).
    const response = await api.post(`/PedidoVendaProdutos/${row.idPedidoVendaProduto}/encerrar`)
    Object.assign(row, response.data)
    const gridRow = itensLocal.value.find(r => r.idPedidoVendaProduto === row.idPedidoVendaProduto)
    if (gridRow) Object.assign(gridRow, response.data)
    emit('show-notification', { message: 'Item encerrado parcialmente e reserva liberada com sucesso.', type: 'success' })
    showItemModal.value = false
    itemModalData.value = null
    itemModalRowIndex.value = null
  } catch (error) {
    const errorData = error.response?.data
    let msg = 'Erro ao encerrar item.'
    if (typeof errorData === 'string') msg = errorData
    else if (errorData?.message) msg = errorData.message
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingAcao.value = false
  }
}

// Resolve o TEXTO de um item de lista do Dicionário de Metadados a partir do
// código (idListaClienteItem). Comparação sempre por String() (regra do
// dicionário de dados). Fallback: devolve o próprio código quando não encontrado.
const resolverTextoLista = (lista, codigo) => {
  if (!codigo) return '-'
  const itens = lista || []
  // Padrão do Dicionário de Dados (regra do projeto): compara por String() e
  // aceita tanto idListaSistemaItem quanto idListaClienteItem — listas
  // estruturais (ex: ESTQ_RESERVA_ST carregada de ReservaEstoqueItem) podem
  // retornar por qualquer uma das fontes.
  const item = itens.find(i =>
    String(i.idListaSistemaItem) === String(codigo) ||
    String(i.idListaClienteItem) === String(codigo)
  )
  return item ? item.textoLista : codigo
}

// Rótulo do modo de custeio — resolvido pelo Dicionário de Metadados
// (PedidoVendaProduto.LstModoCusteio → PED_VEN_MODO_CUSTEIO).
const getModoCusteioLabel = (modo) => resolverTextoLista(listasItem.value.LstModoCusteio, modo)

// Rótulo do tipo do local de estocagem — resolvido pelo Dicionário de Metadados
// (LocalEstoque.LstTipoLocal → ESTQ_TIPO_LOCAL_ESTOCAGEM).
const getTipoLocalLabel = (tipo) => resolverTextoLista(listasItem.value.LstTipoLocal, tipo)

// Resolve o NOME da moeda a partir do código, usando o Dicionário de Metadados.
// `campo` é a chave da lista mapeada (ex.: 'LstMoeda', 'LstMoedaPadrao').
const getMoedaLabel = (codigo, campo) => {
  if (!codigo) return '-'
  const lista = listasMoeda.value[campo] || []
  const item = lista.find(i => String(i.idListaClienteItem) === String(codigo))
  return item ? item.textoLista : codigo
}

const onCellLookup = ({ row, rowIndex, col }) => {
  if (col.field === 'idLocalEstoque' || col.field === 'sku') {
    // Abre o modal de seleção de produto/estoque (não a busca simples de local).
    selecaoEstoqueRowIndex.value = rowIndex
    showModalSelecaoEstoque.value = true
  } else if (col.field === 'lstUnidadeMedidaVenda') {
    buscaUnidadeRowIndex.value = rowIndex
    // Serviços escolhem QUALQUER unidade da lista UN_MED; demais itens usam as
    // conversões cadastradas do produto.
    configBuscaUnidade.value = row?.isServico === true
      ? criarConfigBuscaUnidadeMedidaCliente()
      : criarConfigBuscaUnidadeMedidaProduto(row.idProduto)
    showBuscaUnidadeModal.value = true
  }
}

const handleUnidadeSelecionado = (unidade) => {
  if (!unidade) return
  showBuscaUnidadeModal.value = false
  const rowIndex = buscaUnidadeRowIndex.value
  if (rowIndex == null || rowIndex < 0) return
  const row = itensLocal.value[rowIndex]
  if (row) {
    if (row.isServico === true) {
      // Serviço: unidade vem da lista UN_MED (idListaClienteItem + textoLista).
      // Sem conversão — fator 1.
      row.lstUnidadeMedidaVenda = unidade.idListaClienteItem
      row.descricaoUnidade = unidade.textoLista
      row.fatorConversao = 1
    } else {
      row.lstUnidadeMedidaVenda = unidade.lstUnidadeMedidaTexto
      row.descricaoUnidade = unidade.lstUnidadeMedidaExibicao
      row.fatorConversao = unidade.fatorConversao || 1
      row.lstUnidadeMedidaBase = unidade.lstUnidadeMedidaBase || row.lstUnidadeMedidaBase
    }
  }
  focarCelulaLookup(rowIndex, 'lstUnidadeMedidaVenda')
  // Conclui a edição da célula: dispara o salvamento (e a notificação de êxito)
  concluirEdicaoLinha(row, rowIndex)
}

// Carrega (e cacheia) os locais de estoque e unidades de venda disponíveis para
// um produto. Reutilizado ao aplicar um produto na linha e ao abrir o modal de
// detalhes de um item já persistido.
const carregarDadosProduto = async (idProduto) => {
  const res = await api.get('/PedidoVendaProdutos/preparar-novo', {
    params: { idProduto, idPedidoVenda: props.idPedidoVenda }
  })
  const data = res.data
  locaisPorProduto.value[idProduto] = data.locaisDisponiveis || []
  unidadesPorProduto.value[idProduto] = data.unidadesVenda || []
  return data
}

// Aplica um produto (vindo do autocomplete ou do modal de estoque) na linha.
// `localSelecionado` (opcional) é o local escolhido no modal de seleção de
// produto/estoque; quando ausente, usa o local padrão do pedido.
const aplicarProdutoNaLinha = async (produto, rowIndex, localSelecionado = null) => {
  if (!produto || !produto.idProduto || rowIndex == null || rowIndex < 0) return
  try {
    const data = await carregarDadosProduto(produto.idProduto)
    const unidadesVenda = data.unidadesVenda || []
    // Unidade padrão de VENDA (flag UnidadePadraoVenda). A unidade base NÃO é
    // mais marcada como padrão de venda no backend.
    const unidPadrao = unidadesVenda.find(u => u.unidadePadraoVenda)
    // Fallback: unidade base (quando o produto não tem unidade de venda definida)
    const unidBase = unidadesVenda.find(u => u.lstUnidadeMedida === data.lstUnidadeMedidaBase)
    const unidadeSelecionada = unidPadrao
      ? unidPadrao.lstUnidadeMedida
      : (data.lstUnidadeMedidaBase || '')
    const descricaoUnidadeSelecionada = unidPadrao
      ? (unidPadrao.lstUnidadeMedidaExibicao || unidPadrao.lstUnidadeMedida)
      : (unidBase?.lstUnidadeMedidaExibicao || data.lstUnidadeMedidaBase || '')

    // Atualiza diretamente o itensLocal (reativo) em vez de usar grid.setCellValue
    const row = itensLocal.value[rowIndex]
    if (row) {
      row.idProduto = produto.idProduto
      row.sku = produto.sku || ''
      row.nomeProduto = produto.nome
      row.descricaoProduto = produto.descricao || produto.nome || ''
      // Itens que não controlam estoque (ex.: serviços) têm local opcional.
      row.controlaEstoque = data.controlaEstoque !== false
      // Flag explícito de serviço (descrição editável, estoque bloqueado, unidade UN_MED).
      row.isServico = data.isServico === true
      if (row.isServico) {
        // Serviço não tem local de estoque.
        row.idLocalEstoque = null
        row.descricaoLocal = ''
        // Serviço exige custo simulado (CUST SIMUL) — tal qual estoque Trânsito.
        row.lstModoCusteio = PED_VEN_MODO_CUSTEIO.CustoSimulado
      }

      // Resolve o local de estoque:
      //   • Modal (localSelecionado): usa o local escolhido no modal.
      //   • Dropdown (sem localSelecionado): PRESERVA o local já definido na linha.
      //     A lista suspensa NÃO define o local — o local é apenas filtro de saldo.
      const locais = data.locaisDisponiveis || []
      if (localSelecionado?.idLocalEstoque) {
        let local = locais.find(l => l.idLocalEstoque === localSelecionado.idLocalEstoque) || null
        if (!local) {
          // Local escolhido não está na lista de disponíveis (ex: sem saldo).
          local = {
            idLocalEstoque: localSelecionado.idLocalEstoque,
            descricao: localSelecionado.descricaoLocal || '',
            lstTipoLocal: localSelecionado.lstTipoLocal || null
          }
        }
        row.idLocalEstoque = local.idLocalEstoque
        row.descricaoLocal = local.descricao || ''
        row.lstModoCusteio = derivarModoCusteio(local.lstTipoLocal)
        // Âncoras de origem (1:1) vindas do modal de seleção de produto/estoque.
        // O modal devolve o conjunto completo; a linha guarda a origem escolhida.
        row.idEmbarque = localSelecionado.idEmbarque ?? null
        row.idEmbarqueContainer = localSelecionado.idEmbarqueContainer ?? null
        row.idEmbarquePackingListProduto = localSelecionado.idEmbarquePackingListProduto ?? null
        row.idLoteFabricacao = localSelecionado.idLoteFabricacao ?? null
        // Origem comercial (faturamento/produtor) — âncora semântica da reserva
        row.idDocumentoEntrada = localSelecionado.idDocumentoEntrada ?? null
        row.idDocumentoEntradaItem = localSelecionado.idDocumentoEntradaItem ?? null
      }

      row.lstUnidadeMedidaVenda = unidadeSelecionada
      row.descricaoUnidade = descricaoUnidadeSelecionada
      row.lstUnidadeMedidaBase = data.lstUnidadeMedidaBase
      // Só limpa o custo simulado quando o PRODUTO é trocado (novo SKU).
      // Se apenas o local de estoque foi alterado (mesmo produto), preserva
      // o custo já digitado pelo usuário para não perdê-lo.
      if (row.idProduto !== produto.idProduto) {
        row.custoUnitarioSnapshot = null
      }
      // MargemEsperadaPercentual e MargemEsperadaOrigem são de responsabilidade
      // exclusiva do backend (fonte da verdade), calculados conforme o modo de
      // precificação e a Tabela de Preços — não enviar do frontend.
    }
    recalcularLinha(rowIndex)
    // No modo Por Tabela, resolve o preço automaticamente ao aplicar o produto.
    // (Em Trânsito o preço só é resolvido após o CUST SIMUL ser digitado.)
    if (isModoPorTabela.value) {
      await resolverPrecoProduto(row)
    }
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar dados do produto.', type: 'error' })
  }
}

// Seleção via dropdown de autocomplete do SKU
const onCellAutocompleteSelect = async ({ rowIndex, item }) => {
  await aplicarProdutoNaLinha(item, rowIndex)
  focarCelulaLookup(rowIndex, 'sku')
  concluirEdicaoLinha(itensLocal.value[rowIndex], rowIndex)
}

// Confirmação do modal de seleção de produto/estoque (lupinha).
// O modal devolve `itens[]` já EXPANDIDOS: cada `item` é UMA origem completa
// (âncoras + DE único). A expansão por Documento de Entrada agora é feita DENTRO
// do modal (profundidade dinâmica) — aqui apenas aplicamos cada origem na grade:
//   • 1ª origem → preenche a linha atual.
//   • Demais origens → novas linhas no grid (mesmo produto, âncora própria).
//   • Serviço (isServico) → sem origens de estoque, apenas aplica o produto.
const onConfirmarSelecaoEstoque = async (dados) => {
  if (!dados) return
  const rowIndex = selecaoEstoqueRowIndex.value

  // Serviço: não tem origens de estoque — apenas aplica o produto na linha
  // (sem localSelecionado, o aplicarProdutoNaLinha já trata isServico).
  if (dados.isServico) {
    const produto = { idProduto: dados.idProduto, sku: dados.sku, nome: dados.nome }
    await aplicarProdutoNaLinha(produto, rowIndex)
    focarCelulaLookup(rowIndex, 'sku')
    concluirEdicaoLinha(itensLocal.value[rowIndex], rowIndex)
    return
  }

  const origens = Array.isArray(dados.itens) && dados.itens.length > 0 ? dados.itens : [dados]
  if (origens.length === 0) return

  const produto = { idProduto: dados.idProduto, sku: dados.sku, nome: dados.nome }

  // 1ª origem → preenche a linha atual.
  const primeira = origens.shift()
  await aplicarProdutoNaLinha(produto, rowIndex, primeira)

  // Demais origens → novas linhas no grid (mesmo produto, âncora própria).
  for (const origem of origens) {
    handleAddRow()
    const novoIdx = itensLocal.value.length - 1
    await aplicarProdutoNaLinha(produto, novoIdx, origem)
  }

  focarCelulaLookup(rowIndex, 'sku')
  concluirEdicaoLinha(itensLocal.value[rowIndex], rowIndex)
}

const recalcularLinha = (rowIndex) => {
  const row = itensLocal.value[rowIndex]
  if (!row) return
  const qtd = Number(row.quantidade) || 0
  const unit = Number(row.valorUnitarioOriginal) || 0
  const desc = Number(row.valorTotalDesconto) || 0
  row.valorTotalOriginal = qtd * unit
  row.valorTotalNegociado = Math.max(0, (qtd * unit) - desc)
  if (qtd > 0 && unit > 0) {
    row.valorUnitarioDesconto = desc / qtd
  }
  // A margem NÃO é calculada nem exibida no formulário de vendas: é informação
  // sensível, exibida apenas em relatórios (tratamento no backend).
}

// Resolve o preço de uma linha pela Tabela de Preços do pedido (endpoint de preview).
// O idPrecoComercial pode ser passado explicitamente (cascata) para evitar prop desatualizado.
// O custo é resolvido no backend a partir do LOCAL SELECIONADO:
//   • Final    → custo vigente da Controladoria (ProdutoCusto).
//   • Trânsito → CUST SIMUL digitado pelo usuário (enviado como custoUnitario).
const resolverPrecoProduto = async (row, idPrecoComercial = props.idPrecoComercial) => {
  if (!row?.idProduto || !idPrecoComercial || !row.idLocalEstoque) return
  // Estoque de Trânsito sem CUST SIMUL digitado: não há como resolver o preço.
  if (ehCusteioSimulado(row) && !(Number(row.custoUnitarioSnapshot) > 0)) return
  try {
    const params = {
      idPedidoVenda: props.idPedidoVenda,
      idProduto: row.idProduto,
      idLocalEstoque: row.idLocalEstoque
    }
    if (ehCusteioSimulado(row)) {
      params.custoUnitario = Number(row.custoUnitarioSnapshot)
    }
    const response = await api.get('/PedidoVendaProdutos/resolver-preco', {
      params,
      _suppressLoading: true
    })
    row.valorUnitarioOriginal = response.data?.precoCalculado ?? null
  } catch (e) {
    console.warn('Preço não resolvido para o produto', row.idProduto, e)
    row.valorUnitarioOriginal = null
  }
}

// Orquestrador de cascata: aplica o modo/tabela a todas as linhas e persiste.
//   • Projeto   → limpa preço e desconto (aguarda digitação manual).
//   • Por Tabela → recalcula o preço pela tabela (ou zera se a tabela não estiver definida).
const aplicarModoPrecificacao = async (modo, idPrecoComercial) => {
  const alteradas = []
  for (const row of itensLocal.value) {
    // Itens cancelados são terminais: não recalculam preço/desconto nem são
    // persistidos na cascata (quantidade zerada não deve ser re-precificada).
    if (isLinhaCancelada(row)) continue
    if (modo === PED_VEN_MODO_PRECIFIC.PrecificacaoDeProjeto) {
      row.valorUnitarioOriginal = null
      row.valorTotalDesconto = 0
      row.valorUnitarioDesconto = 0
    } else if (modo === PED_VEN_MODO_PRECIFIC.PrecificacaoPorTabela) {
      if (idPrecoComercial) {
        await resolverPrecoProduto(row, idPrecoComercial)
      } else {
        row.valorUnitarioOriginal = null
      }
    }
    recalcularLinha(itensLocal.value.indexOf(row))
    if (row.idPedidoVendaProduto) alteradas.push(row)
  }
  // Persiste todas as linhas alteradas em uma única operação (evita que o guard
  // isLoadingAcao bloqueie os save-item individuais disparados em cascata).
  if (alteradas.length > 0) emit('save-all-items', alteradas)
}

// Descarta a cópia local pendente de uma linha nova já persistida no servidor.
// Chamado pelo pai após um POST bem-sucedido: o reload traz a versão persistida,
// então a cópia local (sem id) deve sair para não duplicar a linha na grade.
const descartarPendente = (tempId) => {
  if (!tempId) return
  itensLocal.value = itensLocal.value.filter(r => r._tempId !== tempId)
}

defineExpose({ aplicarModoPrecificacao, descartarPendente })

const onCellEdit = async ({ row, rowIndex, field }) => {
  recalcularLinha(rowIndex)
  // Ao digitar o CUST SIMUL (estoque de Trânsito/Serviço) no modo Por Tabela,
  // re-resolve o preço unitário com o custo informado.
  if (field === 'custoUnitarioSnapshot' && isModoPorTabela.value) {
    await resolverPrecoProduto(row)
    recalcularLinha(rowIndex)
  }
  // Linhas novas são persistidas pelo fluxo save-row (concluirEdicaoLinha do grid).
  if (!row.idPedidoVendaProduto) return
  // Linhas salvas: valida os obrigatórios antes de salvar — o custo simulado
  // (serviço/trânsito) é required (custo > 0) e não pode ser limpo/zerado.
  concluirEdicaoLinha(row, rowIndex)
}

// Conclui a edição de uma linha: recalcula e, se os campos obrigatórios
// estiverem preenchidos, dispara o salvamento (auto-save). Usado pelo evento
// save-row do grid e também após seleções em modais (lookup/data/produto).
const concluirEdicaoLinha = (row, rowIndex) => {
  if (!row) return
  // Itens cancelados são terminais: não devem ser revalidados nem persistidos
  // (quantidade/preço zerados são estados legítimos — evita o frontend exigir
  // preenchimento de uma linha cancelada).
  if (isLinhaCancelada(row)) return
  // Itens que não controlam estoque (ex.: serviços) não exigem local de estoque.
  const exigeLocal = row.controlaEstoque !== false
  const obrigatorios = [row.idProduto, exigeLocal ? row.idLocalEstoque : true, row.quantidade > 0, row.valorUnitarioOriginal > 0]
  // Modo CustoSimulado UNIFICADO (serviço ou trânsito): o custo digitado é
  // obrigatório e deve ser maior que zero — custo zero NÃO é aceito.
  if (ehCusteioSimulado(row)) obrigatorios.push(Number(row.custoUnitarioSnapshot) > 0)
  const completo = obrigatorios.every(Boolean)
  // O rowIndex pode ficar defasado após auto-save/recarga do array. Localiza a
  // linha preferencialmente pela referência do objeto, com fallback no índice.
  const idx = itensLocal.value.indexOf(row)
  const alvo = idx >= 0 ? itensLocal.value[idx] : (itensLocal.value[rowIndex] || null)
  if (completo) {
    recalcularLinha(idx >= 0 ? idx : rowIndex)
    if (alvo) alvo._pendingSave = false
    emit('save-item', row)
  } else if (alvo) {
    alvo._pendingSave = true
  }
}

const onSaveRow = ({ row, rowIndex }) => concluirEdicaoLinha(row, rowIndex)

// Remove a linha do array local e, quando for ação EXPLÍCITA do usuário (botão
// de excluir da toolbar) e a linha já estiver persistida, também remove no
// backend. A limpeza automática de linhas vazias (`cleanup: true`) nunca chama
// o backend — caso contrário uma linha recém-adicionada/salva poderia ser
// removida indevidamente no servidor.
const confirmarRemocao = (payload) => {
  const row = payload?.row || payload
  if (!row) return
  const isCleanup = !!payload?.cleanup
  deleteTargetRow.value = row
  const idx = itensLocal.value.findIndex(r =>
    (row._tempId && r._tempId === row._tempId) ||
    (row.idPedidoVendaProduto && r.idPedidoVendaProduto === row.idPedidoVendaProduto)
  )
  if (idx >= 0) itensLocal.value.splice(idx, 1)
  if (!isCleanup && row.idPedidoVendaProduto) {
    emit('delete-item', row.idPedidoVendaProduto)
  }
}

// Cancela a adição de linhas não persistidas (botão de cancelar da toolbar do
// ExcelGrid). Ação EXPLÍCITA do usuário: cancela SEM confirmação.
const cancelarAdicao = () => {
  itensLocal.value = itensLocal.value.filter(r => r.idPedidoVendaProduto)
}

// ============================================================
// Abandonar linha nova na navegação para cima (com confirmação)
// ============================================================
const confirmAbandonRef = ref(null)

// Chamado pelo ExcelGrid quando o usuário tenta subir a partir de uma linha
// nova COM dados: exibe o ConfirmationModal antes de descartá-la.
const onRequestAbandonNewRow = () => {
  confirmAbandonRef.value?.show()
}

// Resposta do ConfirmationModal: confirmar descarta a linha; cancelar mantém.
const onAbandonConfirmed = (confirmado) => {
  if (confirmado) {
    excelGridRef.value?.confirmAbandonRow()
  } else {
    excelGridRef.value?.cancelAbandonRow()
  }
}

// Fechamento sem decisão (X/backdrop): mantém a linha.
const onAbandonClosed = () => {
  excelGridRef.value?.cancelAbandonRow()
}

// Abre o modal de detalhes do item para a linha selecionada no grid. Garante
// que os locais de estoque e unidades de venda do produto estejam carregados
// (necessário para itens já persistidos, cujos mapas ainda não foram populados).
const abrirDetalhesItem = async (row, rowIndex) => {
  if (!row) return
  itemModalRowIndex.value = rowIndex
  itemModalData.value = { ...row }
  // Snapshot dos campos editáveis para detecção de alterações no fechamento.
  itemModalSnapshot.value = {
    percentualTolerancia: row.percentualTolerancia ?? null,
    observacao: row.observacao ?? ''
  }
  if (row.idProduto && !locaisPorProduto.value[row.idProduto]) {
    try {
      await carregarDadosProduto(row.idProduto)
    } catch (e) {
      emit('show-notification', { message: 'Erro ao carregar dados do produto.', type: 'error' })
    }
  }
  showItemModal.value = true
}

const confirmarExcecao = () => {
  if (!itemModalData.value) return
  // Localiza a linha preferencialmente pela referência (id/tempId), com
  // fallback no índice capturado ao abrir o modal.
  const dados = itemModalData.value
  const row = itensLocal.value.find(r =>
    (dados._tempId && r._tempId === dados._tempId) ||
    (dados.idPedidoVendaProduto && r.idPedidoVendaProduto === dados.idPedidoVendaProduto)
  ) || (itemModalRowIndex.value != null ? itensLocal.value[itemModalRowIndex.value] : null)
  if (row) {
    Object.assign(row, dados)
    // Re-deriva o modo de custeio a partir do local escolhido no modal.
    const local = (locaisPorProduto.value[row.idProduto] || [])
      .find(l => l.idLocalEstoque === row.idLocalEstoque)
    if (local) row.lstModoCusteio = derivarModoCusteio(local.lstTipoLocal)
    // Serviço exige custo simulado (CUST SIMUL) — tal qual estoque Trânsito.
    if (row.isServico) row.lstModoCusteio = PED_VEN_MODO_CUSTEIO.CustoSimulado
    const idx = itensLocal.value.indexOf(row)
    recalcularLinha(idx >= 0 ? idx : itemModalRowIndex.value)
    emit('save-item', row)
  }
  showItemModal.value = false
  itemModalData.value = null
  itemModalRowIndex.value = null
  itemModalSnapshot.value = null
}

// Sequencial garante _tempId único por linha (evita colisão de `:key` quando
// duas linhas são adicionadas no mesmo milissegundo — o que impedia a 2ª linha
// de exibir o produto selecionado).
let addRowSeq = 0

// Adiciona uma linha nova ao grid. Os campos numéricos obrigatórios iniciam em
// `0` seguindo a convenção do ExcelGrid: `0` é o sentinela de "vazio" (ver
// arquitetura_excel_grid.md §4.8). Enquanto o usuário não digita nada, a linha
// permanece "em branco" (isRowBlank), o que sustenta o controle de "1 linha em
// adição" e permite descartá-la com segurança ao navegar/cleanup. O salvamento
// real é blindado por concluirEdicaoLinha(), que exige quantidade > 0 e preço
// unitário > 0 antes de emitir save-item.
const handleAddRow = () => {
  addRowSeq += 1
  itensLocal.value.push({
    _tempId: `new-${Date.now()}-${addRowSeq}`,
    idPedidoVendaProduto: null,
    idProduto: null,
    sku: '',
    nomeProduto: '',
    descricaoProduto: '',
    idLocalEstoque: props.idLocalEstoquePadrao,
    descricaoLocal: props.descricaoLocalEstoquePadrao || '',
    // Âncoras de origem (1:1) — preenchidas ao selecionar o estoque via modal.
    idEmbarque: null,
    idEmbarqueContainer: null,
    idEmbarquePackingListProduto: null,
    idLoteFabricacao: null,
    // Origem comercial (faturamento/produtor) — âncora semântica da reserva
    idDocumentoEntrada: null,
    idDocumentoEntradaItem: null,
    lstUnidadeMedidaVenda: '',
    lstModoCusteio: null,
    custoUnitarioSnapshot: null,
    quantidade: 0,
    valorUnitarioOriginal: 0,
    valorTotalDesconto: 0,
    valorTotalNegociado: 0,
    dataPrevisaoEntrega: props.dataPrevisaoEntregaPadrao || null,
    margemEsperadaPercentual: null,
    percentualTolerancia: null,
    observacao: '',
    _pendingSave: true
  })
}
</script>

<template>
  <div class="pedido-venda-itens-excel">

    <ExcelGrid
      ref="excelGridRef"
      :columns="colunas"
      :data="itensLocal"
      row-key="idPedidoVendaProduto"
      has-changes-field="idPedidoVendaProduto"
      :allow-add-rows="!isReadOnly"
      :allow-delete-rows="!isReadOnly"
      :readonly="false"
      :listas="listas"
      empty-message="Nenhum item adicionado."
      modulo-layout="PedidoVendaItens"
      :permitir-selecao-colunas="false"
      @cell-lookup="onCellLookup"
      @cell-autocomplete-select="onCellAutocompleteSelect"
      @cell-edit="onCellEdit"
      @save-row="onSaveRow"
      @delete-row="confirmarRemocao"
      @cancel-all="cancelarAdicao"
      @add-row="handleAddRow"
      @request-abandon-new-row="onRequestAbandonNewRow"
      @show-notification="(p) => emit('show-notification', p)"
    >
      <template #toolbar-actions="{ selectedRow, selectedRowIndex }">
        <!-- O modal de detalhes permanece acessível com permissão (ex.: editar
             observação/status do item, que são exceções ao read-only). -->
        <button v-if="podeEditar" type="button"
                class="excel-grid-detail-btn"
                :disabled="!selectedRow"
                @click="abrirDetalhesItem(selectedRow, selectedRowIndex)"
                title="Editar detalhes do item">
          <i class="bi bi-list-ul"></i>
        </button>
      </template>
    </ExcelGrid>

    <div v-if="itensLocal.length === 0 && (!podeEditar || isPosAprovacao)" class="text-center py-5 text-muted">
      <i class="bi bi-inbox fs-1 d-block mb-2"></i>
      <p class="mb-0">Nenhum item no pedido.</p>
    </div>

    <ConfirmationModal
      ref="confirmAbandonRef"
      title="Descartar linha não salva?"
      message="Esta linha possui dados preenchidos e será <b>descartada</b> ao subir. Deseja realmente continuar?"
      confirm-button-text="Descartar"
      cancel-button-text="Manter linha"
      @confirmed="onAbandonConfirmed"
      @close="onAbandonClosed"
    />

    <ConfirmationModal
      ref="cancelarItemModalRef"
      title="Cancelar Item"
      :message="cancelarItemMessage"
      confirm-button-text="Sim, Cancelar Item"
      cancel-button-text="Não"
      @confirmed="onConfirmCancelarItem"
    />

    <ConfirmationModal
      ref="encerrarItemModalRef"
      title="Encerrar Parcial"
      :message="encerrarItemMessage"
      confirm-button-text="Sim, Encerrar Parcial"
      cancel-button-text="Não"
      @confirmed="onConfirmEncerrarParcial"
    />

    <SelecaoProdutoEstoqueModal
      v-model="showModalSelecaoEstoque"
      :id-produto="selecaoEstoqueRow?.idProduto || null"
      :id-filial="props.idFilial"
      :id-local-estoque="selecaoEstoqueRow?.idLocalEstoque || null"
      :id-organizacao="props.idOrganizacao"
      :lst-tipo-local="selecaoEstoqueRowTipoLocal"
      :id-lote-fabricacao="selecaoEstoqueRow?.idLoteFabricacao || null"
      :id-embarque="selecaoEstoqueRow?.idEmbarque || null"
      :id-embarque-container="selecaoEstoqueRow?.idEmbarqueContainer || null"
      :id-embarque-packing-list-produto="selecaoEstoqueRow?.idEmbarquePackingListProduto || null"
      :id-documento-entrada="selecaoEstoqueRow?.idDocumentoEntrada || null"
      :id-documento-entrada-item="selecaoEstoqueRow?.idDocumentoEntradaItem || null"
      @confirmar="onConfirmarSelecaoEstoque"
      @show-notification="(p) => emit('show-notification', p)"
    />

    <ModalDeBusca
      v-if="showBuscaUnidadeModal"
      :configuracao="configBuscaUnidade"
      @fechar="showBuscaUnidadeModal = false"
      @item-selecionado="handleUnidadeSelecionado"
    />

    <ModalFormularioV2
      v-model="showItemModal"
      :titulo="'Detalhes do Item'"
      cor-header="var(--bs-success)"
      tamanho="xl"
      :loading="isLoadingAcao"
      confirmar-ao-sair
      :tem-alteracoes="itemModalTemAlteracoes"
      @confirmar="confirmarExcecao"
    >
      <!-- Header de identificação (labels, não inputs) -->
      <div class="d-flex flex-wrap align-items-center gap-3 mb-2 p-2 rounded border bg-body-tertiary">
        <div>
          <small class="text-body-secondary d-block lh-1 mb-1">SKU</small>
          <span class="fw-bold">{{ itemModalData?.sku || '-' }}</span>
        </div>
        <div class="flex-grow-1">
          <small class="text-body-secondary d-block lh-1 mb-1">Descrição</small>
          <span class="fw-medium">{{ itemModalData?.descricaoProduto || '-' }}</span>
        </div>
        <div>
          <small class="text-body-secondary d-block lh-1 mb-1">Modo de Custeio</small>
          <span class="fw-medium">{{ getModoCusteioLabel(itemModalData?.lstModoCusteio) }}</span>
        </div>
        <div>
          <small class="text-body-secondary d-block lh-1 mb-1">Status</small>
          <span class="badge" :class="badgeStatusItem(itemModalData?.lstStatusPedidoItem)">
            {{ getStatusItemLabel(itemModalData?.lstStatusPedidoItem) }}
          </span>
        </div>
      </div>

      <!-- Botões de ação para transição de status do item -->
      <div v-if="podeCancelarItem || podeEncerrarParcial" class="d-flex gap-2 mb-3">
        <button v-if="podeCancelarItem" type="button" class="btn btn-outline-danger btn-sm"
          @click="acaoCancelarItem">
          <i class="bi bi-x-circle me-1"></i> Cancelar Item
        </button>
        <button v-if="podeEncerrarParcial" type="button" class="btn btn-outline-warning btn-sm"
          @click="acaoEncerrarParcial">
          <i class="bi bi-check-circle me-1"></i> Encerrar Parcial
        </button>
      </div>

      <!-- CARD: DETALHES (campos editáveis do item) -->
      <div class="card mb-3 border-success-subtle shadow-sm">
        <div class="card-header bg-success-subtle py-2">
          <h6 class="mb-0 text-success-emphasis fw-bold">
            <i class="bi bi-pencil-square me-2"></i>Detalhes
          </h6>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-3">
              <CampoInput id="itemPercentualTolerancia" label="Tolerância %" v-model="itemModalData.percentualTolerancia"
                type="percentual" :disabled="!campoEditavelItem('percentualTolerancia')" />
            </div>
            <div class="col-md-9">
              <CampoInput id="itemObservacao" label="Observação" v-model="itemModalData.observacao"
                type="textarea" :max-length="300" :disabled="false" />
            </div>
          </div>
        </div>
      </div>

      <!-- CARD: QUANTITATIVOS -->
      <div class="card mb-3 border-success-subtle shadow-sm">
        <div class="card-header bg-success-subtle py-2">
          <h6 class="mb-0 text-success-emphasis fw-bold">
            <i class="bi bi-sort-numeric-down me-2"></i>Quantitativos
          </h6>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-3">
              <CampoInput label="Qtd Original" :model-value="formatarQtd(itemModalData?.quantidade)" disabled />
            </div>
            <div class="col-md-3">
              <CampoInput label="Qtd Faturada" :model-value="formatarQtd(itemModalData?.quantidadeFaturada)" disabled />
            </div>
            <div class="col-md-3">
              <CampoInput label="Qtd Cancelada" :model-value="formatarQtd(itemModalData?.quantidadeCancelada)" disabled />
            </div>
            <div class="col-md-3">
              <CampoInput label="Qtd Pendente" :model-value="formatarQtd(calcularQtdPendente(itemModalData))" disabled />
            </div>
          </div>
        </div>
      </div>

      <!-- CARD: VALORES -->
      <div class="card mb-3 border-success-subtle shadow-sm">
        <div class="card-header bg-success-subtle py-2">
          <h6 class="mb-0 text-success-emphasis fw-bold">
            <i class="bi bi-currency-dollar me-2"></i>Valores
          </h6>
        </div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table table-sm table-bordered align-middle mb-0">
              <thead class="bg-body-tertiary">
                <tr>
                  <th scope="col" class="small text-uppercase text-body-secondary fw-semibold">Valores</th>
                  <th scope="col" class="text-end">{{ getMoedaLabel(lstMoeda, 'LstMoeda') }}</th>
                  <th scope="col" class="text-end">{{ getMoedaLabel(lstMoedaPadrao, 'LstMoedaPadrao') }}</th>
                  <th scope="col" class="text-end">{{ getMoedaLabel(lstMoedaGerencial, 'LstMoedaGerencial') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row" class="text-body-secondary fw-normal">Original</th>
                  <td class="text-end">{{ formatarMoeda(itemModalData?.valorTotalOriginal) }}</td>
                  <td class="text-end">{{ formatarMoeda(itemModalData?.valorTotalOriginalPadrao) }}</td>
                  <td class="text-end">{{ formatarMoeda(itemModalData?.valorTotalOriginalGerencial) }}</td>
                </tr>
                <tr>
                  <th scope="row" class="text-body-secondary fw-normal">Desconto</th>
                  <td class="text-end">{{ formatarMoeda(itemModalData?.valorTotalDesconto) }}</td>
                  <td class="text-end">{{ formatarMoeda(itemModalData?.valorTotalDescontoPadrao) }}</td>
                  <td class="text-end">{{ formatarMoeda(itemModalData?.valorTotalDescontoGerencial) }}</td>
                </tr>
                <tr class="table-active fw-bold">
                  <th scope="row">Negociado</th>
                  <td class="text-end">{{ formatarMoeda(itemModalData?.valorTotalNegociado) }}</td>
                  <td class="text-end">{{ formatarMoeda(itemModalData?.valorTotalNegociadoPadrao) }}</td>
                  <td class="text-end">{{ formatarMoeda(itemModalData?.valorTotalNegociadoGerencial) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- CARD: RESERVA / PRÉ-CONFIGURAÇÃO / SALDO -->
      <div class="card border-success-subtle shadow-sm">
        <div class="card-header bg-success-subtle py-2 d-flex align-items-center gap-2">
          <h6 class="mb-0 text-success-emphasis fw-bold">
            <i class="bi bi-box-seam me-2"></i>{{ tituloCardReserva }}
          </h6>
          <span v-if="reservaDoItem" class="badge" :class="badgeStatusReserva(reservaDoItem.lstStatusItemReserva)">
            {{ getStatusReservaLabel(reservaDoItem.lstStatusItemReserva) }}
          </span>
        </div>
        <div class="card-body">
          <template v-if="reservaDoItem">
            <div class="row g-3">
              <div class="col-md-3">
                <CampoInput label="Local de Estoque" :model-value="itemModalData?.descricaoLocal || '-'" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput :label="labelQtdReservadaReserva" :model-value="formatarQtd(reservaDoItem.quantidadeReservadaUnBasica)" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput label="Embarque" :model-value="exibirId(reservaDoItem.idEmbarque)" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput label="Contêiner" :model-value="exibirId(reservaDoItem.idEmbarqueContainer)" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput label="Packing List Produto" :model-value="exibirId(reservaDoItem.idEmbarquePackingListProduto)" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput label="Lote de Fabricação" :model-value="exibirId(reservaDoItem.idLoteFabricacao)" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput label="Documento de Entrada" :model-value="exibirId(reservaDoItem.idDocumentoEntrada)" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput label="Item do DE" :model-value="exibirId(reservaDoItem.idDocumentoEntradaItem)" disabled />
              </div>
            </div>
          </template>
          <template v-else-if="itemReservado">
            <small class="text-body-secondary d-block mb-2">A reserva é efetivada na confirmação da venda.</small>
            <div class="row g-3">
              <div class="col-md-3">
                <CampoInput label="Filial" :model-value="descricaoFilial || '-'" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput label="Local de Estoque" :model-value="itemModalData?.descricaoLocal || '-'" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput label="Tipo do Local" :model-value="getTipoLocalLabel(localSelecionadoModal?.lstTipoLocal)" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput label="Embarque" :model-value="exibirId(itemModalData?.idEmbarque)" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput label="Contêiner" :model-value="exibirId(itemModalData?.idEmbarqueContainer)" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput label="Packing List Produto" :model-value="exibirId(itemModalData?.idEmbarquePackingListProduto)" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput label="Lote de Fabricação" :model-value="exibirId(itemModalData?.idLoteFabricacao)" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput label="Documento de Entrada" :model-value="exibirId(itemModalData?.idDocumentoEntrada)" disabled />
              </div>
              <div class="col-md-3">
                <CampoInput label="Item do DE" :model-value="exibirId(itemModalData?.idDocumentoEntradaItem)" disabled />
              </div>
            </div>
          </template>
          <template v-else>
            <div class="row g-3">
              <div class="col-md-4">
                <CampoInput label="Local de Estoque" :model-value="itemModalData?.descricaoLocal || '-'" disabled />
              </div>
              <div class="col-md-4">
                <CampoInput label="Tipo do Local" :model-value="getTipoLocalLabel(localSelecionadoModal?.lstTipoLocal)" disabled />
              </div>
              <div class="col-md-4">
                <CampoInput label="Saldo disponível" :model-value="formatarQtd(saldoLocalSelecionado)" disabled />
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Footer padrão do ModalFormularioV2 (Cancelar → tentarFechar respeitando
           temAlteracoes; Salvar → confirmar → confirmarExcecao). -->
    </ModalFormularioV2>
  </div>
</template>

<style scoped>
.pedido-venda-itens-excel {
  padding: 0;
}

/* Botão de detalhes do item na toolbar do ExcelGrid (slot toolbar-actions) */
.excel-grid-detail-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  background: transparent;
  color: var(--bs-success);
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
  padding: 0;
  line-height: 1;
}

.excel-grid-detail-btn:hover:not(:disabled) {
  background: var(--bs-success-bg-subtle);
  color: var(--bs-success-text-emphasis);
  border-color: var(--bs-success);
}

.excel-grid-detail-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
